import React from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { Button, ButtonVariantType } from "../../primitives";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";

export interface FallbackUIProps {
  error: Error;
  resetErrorBoundary: () => void;
  isGlobal?: boolean;
}

const pulseAnim = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
`;

const floatAnim = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

export const FallbackUI: React.FC<FallbackUIProps> = ({
  error,
  resetErrorBoundary,
  isGlobal = false,
}) => {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: isGlobal ? "100vh" : "calc(100vh - 140px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background: isGlobal ? "radial-gradient(circle at center, #f8f9fa 0%, #eef2f6 100%)" : "transparent",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "560px",
          p: { xs: 3, md: 5 },
          textAlign: "center",
        }}
      >
        {/* Animated Icon */}
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 30px",
            animation: `${pulseAnim} 2.5s infinite, ${floatAnim} 3s ease-in-out infinite`,
            boxShadow: "0 8px 32px rgba(239, 68, 68, 0.25)",
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: 48,
              color: "#dc2626",
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant={isGlobal ? "h3" : "h4"}
          sx={{
            fontWeight: 800,
            background: "linear-gradient(90deg, #111827, #374151)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 2,
            letterSpacing: "-0.02em",
          }}
        >
          Oops! Something went wrong
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body1"
          sx={{
            color: "#6b7280",
            mb: 4,
            fontSize: "1.05rem",
            maxWidth: "420px",
            margin: "0 auto 36px",
            lineHeight: 1.6,
          }}
        >
          We encountered an unexpected error. Please try again or refresh the
          page to continue.
        </Typography>

        {/* Error Message */}
        {!!error?.message && (
          <Box
            sx={{
              bgcolor: "#ffececff",

              px: 2,
              py: 1.5,
              mb: 4,
              textAlign: "left",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                display: "block",
                mb: 0.5,
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              ERROR DETAILS
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#374151",
                wordBreak: "break-word",
              }}
            >
              {error?.message}
            </Typography>
          </Box>
        )}

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            justifyContent: "center",
            flexWrap: "wrap",
            mt: 2,
          }}
        >
          <Button
            variantType={ButtonVariantType.PRIMARY}
            startIcon={<ReplayIcon />}
            onClick={resetErrorBoundary}

          >
            Try Again
          </Button>

          <Button
            variantType={ButtonVariantType.PRIMARY_OUTLINED}
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh Page
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
